import { computed, getCurrentInstance, nextTick, ref } from 'vue';
import { themes } from '@/styles/themes';

const STORAGE_KEY = 'theme';
const names = Object.keys(themes);

function loadSaved() {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (names.includes(saved)) return saved;
    } catch {
        // Storage blocked (private browsing...): use the default.
    }
    return names[0];
}

/** Name of the active variant of `themes`, shared by every component. */
export const themeVariant = ref(loadSaved());

/**
 * The active color variant, and `select` to switch to another one: swaps Vuetify's colors,
 * cross-fades the page where supported, and remembers the choice for the next visit.
 */
export function useThemeVariant() {
    const { $vuetify } = getCurrentInstance().proxy;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function apply(name) {
        themeVariant.value = name;
        $vuetify.theme.setTheme('dark', themes[name].colors);
        try {
            localStorage.setItem(STORAGE_KEY, name);
        } catch {
            // Not remembered, but still switched.
        }
        // Lets the cross-fade wait for [data-theme] to be rendered.
        return nextTick();
    }

    function select(name) {
        if (reducedMotion || !document.startViewTransition) apply(name);
        else document.startViewTransition(() => apply(name));
    }

    return {
        variant: themeVariant,
        // The variant after the active one, for a toggle cycling through them.
        next: computed(() => names[(names.indexOf(themeVariant.value) + 1) % names.length]),
        select,
    };
}
